import React, { useEffect } from 'react';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { RCListingGrid, useMediaQuery } from '@reverbdotcom/cadence/components';
import { useIntersectionTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { OverflowingRow } from '@reverbdotcom/commons';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { isEmpty } from 'lodash';

import {
  Input_reverb_search_ListingsSearchRequest as ListingsSearchRequest,
  reverb_search_ListingsSearchRequest_Sort as Sort,
  reverb_search_ListingsSearchRequest_FeaturedWinnerRank as FeaturedWinnerRank,
} from '@reverbdotcom/commons/src/gql/graphql';

const translate = withPrefix('discovery.marketplace.featuredListingsRow');

export const COMPONENT_NAME = 'FeaturedListingsRow';
export const MIN_MOBILE_LISTINGS = 2;
export const MIN_TABLET_LISTINGS = 3;
export const MIN_DESKTOP_LISTINGS = 5;
export const MIN_MARKETPLACE_LISTINGS = 30;
export const MAX_LISTINGS = 15;

function getMinListings(isMobile: boolean, isTablet: boolean) {
  if (isMobile) return MIN_MOBILE_LISTINGS;
  if (isTablet) return MIN_TABLET_LISTINGS;
  return MIN_DESKTOP_LISTINGS;
}

interface FeaturedListingsRowProps {
  searchParams: ListingsSearchRequest;
}

export function FeaturedListingsRow({ searchParams }: FeaturedListingsRowProps) {
  const isMobile = useMediaQuery('mobile');
  const isTablet = useMediaQuery('tablet');

  // Fetch featured listings of CPs related to the marketplace query
  const { data, loading } = useQuery(FEATURED_LISTINGS_QUERY, {
    variables: {
      input: {
        ...searchParams,
        cpNewConditionAndTnpFeaturedWinnerRanks: [FeaturedWinnerRank.WINNER],
        sort: Sort.LIKELIHOOD_TO_SELL,
        limit: MAX_LISTINGS,
      },
    },
  });

  const featuredListings = data?.listingsSearch?.listings ?? [];
  const [bulkTrackImpressions] = useMutation(BUMP_TRACKING_MUTATION);

  // Track bump impressions for displayed listings that are bumped
  useEffect(() => {
    const bumpedListingIds = featuredListings.filter((l) => l.bumped).map((l) => l.id);
    if (featuredListings.length < minListings || isEmpty(bumpedListingIds)) return;

    bulkTrackImpressions({ variables: { input: { listingIds: bumpedListingIds } } });
  }, [featuredListings]);

  const trackingRef = useIntersectionTracking(
    {
      componentName: COMPONENT_NAME,
      eventName: MParticleEventName.ViewedFeaturedListingsRow,
      listings: featuredListings,
    },
    true, // Should track
  );

  const minListings = getMinListings(isMobile, isTablet);
  if (featuredListings.length < minListings) return null;

  return (
    <section data-testid="featured-listings-row" className="featured-listing-row" ref={trackingRef} >
      <OverflowingRow
        title={translate('title')}
        subtitle={translate('subtitle')}
        collectionCount={featuredListings.length}
      >
        <RCListingGrid loading={loading} singleRow>
          {featuredListings.map((listing, idx) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              position={idx}
              listingsCount={featuredListings.length}
              trackingName={COMPONENT_NAME}
              showShippingDisplay
            />
          ))}
        </RCListingGrid>
      </OverflowingRow>
    </section>
  );
}

export const BUMP_TRACKING_MUTATION = gql(`
  mutation Core_Marketplace_FeaturedListingsBumpTracking($input: Input_arbiter_BulkTrackImpressionsRequest) {
    bulkTrackImpressions(input: $input) {
      listingIds
    }
  }
`);

export const FeaturedListingsFragment = gql(`
  fragment FeaturedListingsFields on Listing {
    _id
    ...BumpKey
    ...ListingCardFields
  }
`);

export const FEATURED_LISTINGS_QUERY = gql(`
  query Core_Marketplace_FeaturedListingsSearch(
    $input: Input_reverb_search_ListingsSearchRequest!
  ) {
    listingsSearch(input: $input) {
      listings {
        ...FeaturedListingsFields
      }
    }
  }
`);

export default FeaturedListingsRow;
